import React from 'react';
import FocusLock from 'react-focus-lock';
import { cn } from '../../utils/cn';

interface FocusedDialogProps {
  /** ARIA role. Defaults to `dialog`. */
  role?: 'dialog' | 'alertdialog';
  /** Accessible label for screen readers. */
  ariaLabel?: string;
  /** Defaults to `true`. Set to `false` for non-modal popovers. */
  ariaModal?: boolean;
  /** Tailwind classes applied to the inner dialog div. */
  className?: string;
  /** Optional ref to the dialog container (e.g. for caller-side positioning measurements). */
  innerRef?: React.Ref<HTMLDivElement>;
  /** Optional inline style (e.g. for floating popover positioning). */
  style?: React.CSSProperties;
  /** Mouse-down handler — used by popovers to stop the document-level outside-click handler. */
  onMouseDown?: React.MouseEventHandler<HTMLDivElement>;
  /** Drag-start handler — used by popovers to prevent text-drag side effects. */
  onDragStart?: React.DragEventHandler<HTMLDivElement>;
  children: React.ReactNode;
}

/**
 * Shared focus-trap wrapper for dialog/popover/drawer surfaces.
 *
 * Encapsulates the four-attribute coordination that every modal-like surface
 * needs to behave correctly for keyboard + screen-reader users:
 *
 *   1. `<FocusLock returnFocus autoFocus className="contents">` wraps the
 *      content in `react-focus-lock`. `contents` makes the wrapper
 *      layout-transparent so the outer flex-centring (Modal) or fixed
 *      positioning (Popover/Drawer) keeps working unchanged.
 *   2. `data-autofocus` tells FocusLock to land initial focus on the dialog
 *      container itself — NOT on the first interactive child. Without this,
 *      modal bodies that use `role="button"` rows would have one row
 *      pre-highlighted on open, which reads as a stray selection.
 *   3. `tabIndex={-1}` makes the dialog programmatically focusable so the
 *      `data-autofocus` target is valid, but keeps it out of the Tab order.
 *   4. `focus:outline-none` suppresses the default browser focus ring on the
 *      container (which isn't interactive — it's just a focus parking spot).
 *
 * Behaviour preserved:
 *   - Tab / Shift+Tab cycle inside the dialog (the trap itself).
 *   - `returnFocus` restores focus to the trigger element on close.
 *   - All ARIA attributes (`role`, `aria-modal`, `aria-label`) pass through
 *     onto the dialog div so screen readers identify the surface correctly.
 *
 * Adopt for: `Modal`, `ColumnFilterPopover`, `SubscriberDrilldownDrawer`, and
 * any future modal-like surface (drawers, command palettes, etc.).
 */
export const FocusedDialog: React.FC<FocusedDialogProps> = ({
  role = 'dialog',
  ariaLabel,
  ariaModal = true,
  className,
  innerRef,
  style,
  onMouseDown,
  onDragStart,
  children,
}) => (
  <FocusLock returnFocus autoFocus className="contents">
    <div
      ref={innerRef}
      tabIndex={-1}
      data-autofocus
      role={role}
      aria-modal={ariaModal}
      aria-label={ariaLabel}
      style={style}
      onMouseDown={onMouseDown}
      onDragStart={onDragStart}
      className={cn('focus:outline-none', className)}
    >
      {children}
    </div>
  </FocusLock>
);

export default FocusedDialog;
